import * as React from 'react';
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router';
import {
  captureException,
  captureMessage,
  getCurrentScope,
  globalHandlersIntegration,
  init,
  withScope,
} from '@sentry/react';
import { isSupportedBrowser } from './app/supportedBrowsers';

const TRACING_SAMPLE_RATE = 0.1;

type GetReplay = Awaited<NonNullable<Window['Sentry']>['replayReady']>;

let markReplayReady: (getReplay: GetReplay) => void;

function initSentry({ enableTracing }: { enableTracing: boolean }) {
  const shouldEnableSentry = process.env.NODE_ENV === 'production';
  const { __slab } = window;
  const slabUrlRegex = new RegExp(`https://(.+\\.)?${__slab.DOMAIN}`, 'i');

  init({
    enabled: shouldEnableSentry,
    attachStacktrace: true, // so `captureMessage` also have a stacktrace
    debug: false,
    release: __slab.VERSION,
    allowUrls: [document.location.host, slabUrlRegex],
    dsn: 'https://df15549ba190f45040e8ac277c3a8ffa@o59832.ingest.sentry.io/4505782805528576',
    environment: __slab.ENVIRONMENT,
    replaysOnErrorSampleRate: shouldEnableSentry ? 1 : 0,
    replaysSessionSampleRate: 0,
    tracesSampleRate: enableTracing ? 1 : 0,
    integrations: [
      globalHandlersIntegration({
        onerror: true,
        // We log non-apollo errors in app.tsx in the unhandledrejection event listener and apollo errors in getErrorLink
        onunhandledrejection: false,
      }),
    ],
    initialScope: {
      tags: {
        browserSupported: isSupportedBrowser(),
      },
    },
  });

  window.Sentry = {
    isEnabled: shouldEnableSentry,
    withScope,
    captureMessage,
    captureException,
    getCurrentScope,
    // Created here so it is readable before `initReplay` has loaded; consumers
    // wait on it instead of racing the chunk.
    replayReady: new Promise<GetReplay>((resolve) => {
      markReplayReady = resolve;
    }),
  };
}

async function main() {
  const isTracingEnabled = Math.random() <= TRACING_SAMPLE_RATE;
  initSentry({ enableTracing: isTracingEnabled });

  try {
    const [{ default: app }, { default: initTracing }] = await Promise.all([
      import(
        /* webpackChunkName: "app" */
        './app/app'
      ),
      window.Sentry?.isEnabled && isTracingEnabled
        ? await import('./initTracing').catch((e) => {
            captureException(e, {
              level: 'error',
            });
            return { default: null };
          })
        : Promise.resolve({ default: null }),
    ]);

    initTracing?.({
      useEffect: React.useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    });
    await app();
  } catch (e) {
    const isSupportedBrowserResult = isSupportedBrowser();

    captureException(e, {
      level: isSupportedBrowserResult ? 'fatal' : 'error',
    });

    const browserPrompt = isSupportedBrowserResult
      ? '<p>The error has been logged and we’ll be on it!</p>'
      : '<p>Your browser may be outdated or not supported by Slab.</p>';

    const root = document.getElementById('root') || document.body;

    if (root) {
      root.innerHTML = `
      <div style="margin:12px;font-family:sans-serif">
        <h2>Oops! Something went wrong.</h2>
        ${browserPrompt}
      </div>
    `;
    }

    throw e;
  }

  const initReplay = (
    await import('./initReplay').catch((e) => {
      captureException(e, {
        level: 'error',
      });
      return { default: null };
    })
  ).default;

  const getReplay = initReplay?.();
  if (getReplay) {
    markReplayReady(getReplay);
  }
}

main();
